'use client';

import React from 'react';
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import Icon from '@/components/ui/AppIcon';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { useShop } from '@/components/shop/ShopProvider';
import { usePathname } from '@/i18n/navigation';
import { PromoBar } from '@/components/shop/Promo';

const navLinks = [
  { key: 'product', href: '#producto' },
  { key: 'how', href: '#como-funciona' },
  { key: 'manual', href: '/funcionamiento' },
  { key: 'faq', href: '#faq' },
] as const;

export default function Header() {
  const t = useTranslations('header');
  const { selectedVariant, add, openCart, cart, cartBump } = useShop();
  const cartCount = cart?.totalQuantity ?? 0;
  // Section anchors live on the home page; elsewhere (product page) they point back to it
  const locale = useLocale();
  const isHome = usePathname() === '/';
  const home = isHome ? '' : `/${locale}`;
  // "#..." links are sections of the home page; "/..." links are pages of their own
  const linkHref = (href: string) =>
    href.startsWith('#') ? `${home}${href}` : `/${locale}${href}`;
  const [scrolled, setScrolled] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  React.useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    <>
      <header
        // Transparent over the hero; once scrolled, translucent black with a very light blur and a
        // hairline border (no shadow)
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-700 border-b ${
          scrolled
            ? 'bg-[rgba(11,9,7,0.72)] backdrop-blur-md border-[rgba(242,235,224,0.08)]'
            : 'bg-transparent border-transparent'
        }`}
      >
        {/* Over the photo only: a soft black fade behind the whole header (no bar, no box) so the
            logo and links stay readable over the bright sunrise. Fades out once scrolled. */}
        <div
          aria-hidden="true"
          className={`absolute inset-x-0 top-0 h-[120px] -z-10 pointer-events-none transition-opacity duration-700 ${
            scrolled ? 'opacity-0' : 'opacity-100'
          }`}
          style={{
            background:
              'linear-gradient(to bottom, rgba(5,4,3,0.48) 0px, rgba(5,4,3,0.2) 62px, rgba(5,4,3,0) 115px)',
          }}
        />
        <PromoBar collapsed={scrolled} />
        <div
          className={`max-w-[146rem] mx-auto px-7 sm:px-8 lg:px-12 h-14 md:h-16 lg:h-[70px] grid grid-cols-[1fr_auto_1fr] items-center ${
            scrolled ? '' : '[text-shadow:0_1px_3px_rgba(0,0,0,0.35)]'
          }`}
        >
          {/* Nav (left on desktop) */}
          <nav className="hidden lg:flex items-center lg:gap-6 xl:gap-[30px]">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={linkHref(link.href)}
                className={`label-mono lg:text-[11px] whitespace-nowrap transition-colors duration-300 ${
                  link.key === 'manual'
                    ? 'text-[var(--copper)] hover:text-[var(--cream)]'
                    : 'text-[rgba(242,235,224,0.72)] hover:text-[var(--cream)]'
                }`}
              >
                {t(`nav.${link.key}`)}
              </a>
            ))}
          </nav>
          <div className="lg:hidden" />

          {/* Logo (centered) */}
          <a
            href={isHome ? '#' : home}
            aria-label={t('home')}
            className="flex items-center"
            onClick={() => isHome && window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <Image
              src="/assets/images/nomafe-logo.png"
              alt="Nomafé"
              width={960}
              height={337}
              priority
              sizes="130px"
              className="h-10 sm:h-12 lg:h-[35px] w-auto transition-[filter] duration-700"
              // Over the photo: ~12% brighter copper and a barely-there shadow, purely for legibility
              style={{
                filter: scrolled
                  ? 'none'
                  : 'brightness(1.12) drop-shadow(0 1px 5px rgba(0,0,0,0.55))',
              }}
            />
          </a>

          {/* Actions (right) */}
          <div className="flex items-center justify-end gap-2 sm:gap-5 lg:gap-[22px]">
            <LanguageSwitcher />
            {/* Buys the real Shopify variant; falls back to the product sheet if it can't be bought */}
            <a
              href={`${home}#producto`}
              onClick={(e) => {
                if (!selectedVariant?.availableForSale) return;
                e.preventDefault();
                add();
              }}
              className="hidden md:inline-flex label-mono lg:text-[11px] text-[var(--cream)] items-center gap-2 group"
            >
              <span className="border-b border-[rgba(242,235,224,0.35)] group-hover:border-[var(--copper)] pb-0.5 transition-colors">
                {t('buy')}
              </span>
            </a>
            <button
              type="button"
              onClick={openCart}
              // Landing point of the add-to-cart coffee bean (see shop/beanFlight.ts)
              data-cart-icon=""
              aria-label={cartCount > 0 ? `${t('cart')} (${cartCount})` : t('cart')}
              className={`relative w-9 h-9 flex items-center justify-center text-[rgba(242,235,224,0.85)] hover:text-[var(--copper)] transition-colors duration-300 ${
                scrolled ? '' : '[filter:drop-shadow(0_1px_3px_rgba(0,0,0,0.35))]'
              }`}
            >
              {/* Re-keyed on every landing so the small pulse replays (not on page load) */}
              <span key={`bump-${cartBump}`} className={`inline-flex ${cartBump ? 'cart-bump' : ''}`}>
                <Icon name="ShoppingBagIcon" size={15} variant="outline" />
              </span>
              {cartCount > 0 && (
                <span
                  key={`count-${cartCount}`}
                  aria-hidden="true"
                  className={`${cartBump ? 'cart-count-in ' : ''}absolute top-0.5 right-0 min-w-[15px] h-[15px] px-1 rounded-full bg-[var(--copper)] text-[var(--ink)] font-mono text-[9px] leading-[15px] text-center`}
                >
                  {cartCount}
                </span>
              )}
            </button>
            <button
              className={`lg:hidden w-9 h-9 flex items-center justify-center text-[rgba(242,235,224,0.85)] ${
                scrolled ? '' : '[filter:drop-shadow(0_1px_3px_rgba(0,0,0,0.35))]'
              }`}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label={menuOpen ? t('closeMenu') : t('openMenu')}
              aria-expanded={menuOpen}
            >
              <Icon name={menuOpen ? 'XMarkIcon' : 'Bars2Icon'} size={16} variant="outline" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <div
        className={`fixed inset-0 z-40 bg-[var(--ink)] flex flex-col justify-between px-6 pt-28 pb-10 transition-[opacity,visibility] duration-500 lg:hidden ${
          menuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <nav className="flex flex-col">
          {navLinks.map((link, i) => (
            <a
              key={link.href}
              href={linkHref(link.href)}
              onClick={() => setMenuOpen(false)}
              className="flex items-baseline gap-4 py-4 border-b border-[var(--line)]"
            >
              <span className="label-mono text-copper">0{i + 1}</span>
              <span className={`display text-[2.6rem] ${link.key === 'manual' ? 'text-copper' : ''}`}>
                {t(`nav.${link.key}`)}
              </span>
            </a>
          ))}
        </nav>
        <a href={`${home}#producto`} onClick={() => setMenuOpen(false)} className="btn btn-solid w-full">
          {t('discover')}
          <span aria-hidden="true">→</span>
        </a>
      </div>
    </>
  );
}
